import React, { FC } from 'react';

import { IWidget, ParsedEntry } from '@/contentful/types';

import {
  IncidentClassification,
  IncidentSubClassification,
} from '@models/Incident';
import { isLocalOrTestEnv } from '@utils/featureFlags';

import { TitleDescriptionIncidentForm } from './components/TitleDescriptionIncidentForm';
import { apartmentDefectForms } from './Defect/ApartmentDefect';
import { buildingDefectForms } from './Defect/BuildingDefect';
import { contractForms } from './Contract';
import { disturbanceForms } from './Disturbance';
import { keysForms } from './Keys';
import {
  MessageFormKeyType,
  OtherFormKey,
  ParkingFormKey,
  SaunaFormKey,
} from './MessageForms.types';
import { ParkingOtherMessageForm, ParkingRentingQuestionForm } from './Parking';
import { paymentForms } from './Payments';
import { renovationForms } from './Renovation';
import { SaunaMessageForm } from './Sauna';

type MessageFormType = {
  formId: MessageFormKeyType;
};

const formMap: Partial<Record<MessageFormKeyType, FC<ParsedEntry<IWidget>>>> = {
  // Contracts
  ...contractForms,

  // Payments
  ...paymentForms,

  // Renovation
  ...renovationForms,

  // Disturbance
  ...disturbanceForms,

  // Defects
  ...buildingDefectForms,
  ...apartmentDefectForms,

  // Keys
  ...keysForms,

  // Parking
  [ParkingFormKey.PARKING_OTHER_ISSUE]: ParkingOtherMessageForm,
  [ParkingFormKey.PARKING_RENTING_QUESTION]: ParkingRentingQuestionForm,

  // Sauna
  [SaunaFormKey.SAUNA_RESERVATION]: SaunaMessageForm,

  // Other
  [OtherFormKey.OTHER]: ({ customTranslations }) => (
    <TitleDescriptionIncidentForm
      incidentKind="Other"
      withFileUpload
      translations={customTranslations}
      classifications={{
        classification: IncidentClassification.SUPPORT,
        subClassification: IncidentSubClassification.OTHER_REASON,
      }}
    />
  ),
};

const isValidParameters = (obj: unknown): obj is MessageFormType =>
  typeof obj === 'object' && !!obj && 'formId' in obj;

export const ResolveMessageForm = ({
  data,
}: {
  data: ParsedEntry<IWidget>;
}) => {
  if (!isValidParameters(data.parameters)) {
    if (isLocalOrTestEnv) return <div>Invalid or missing formId</div>;

    return null;
  }

  const FormElement = formMap[data.parameters.formId];

  if (!FormElement) {
    if (isLocalOrTestEnv) {
      return (
        <div>
          No forms found with the provided formId: {data.parameters.formId}
        </div>
      );
    }

    return null;
  }

  return <FormElement {...data} />;
};
